import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import parse from 'html-react-parser';
import { getWithAuth } from '@/utils/apiClient';
import { buildSendPayload } from '@/utils/noteSend';
import { getNoteTypeLabel } from '@/features/notes/utils/noteTypeUtils';

import { nowUtc } from './../../../utils/dateUtils';
import { logger } from "../../../utils/logger";
import { WELCOME_MESSAGES, WELCOME_OPTIONS } from '../constants';

export function useMessageActions({
  input,
  setInput,
  setMessages,
  noteMode,
  selectedNoteTypes,
  contextSendMessage,
  currentSessionId,
  currentPatient,
  currentEncounter,
  setError,
  setCurrentPage,
  setHasMoreMessages,
  addNoteCreatedNotification,
  setViewingNoteInline,
  setInlineNoteData,
  messagesEndRef
}) {
  const [noteError, setNoteError] = useState(null);
  const [successMessages, setSuccessMessages] = useState([]);
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);
  const [draftEditConfirmed, setDraftEditConfirmed] = useState(false);

  useEffect(() => {
    setDraftEditConfirmed(false);
  }, [currentEncounter?.note_id]);

  const clearCurrentRequestId = useCallback(() => {
    setCurrentRequestId(null);
  }, []);

  const sendMessage = useCallback(async (messageText = input, notePrefsOverride = null, overrideContext = null) => {
    const textToSend = messageText.trim();
    if (!textToSend) return;

    if (currentEncounter?.note_id && !draftEditConfirmed) {
      if (/^(yes|y|edit|edit it|confirm|confirmed)$/i.test(textToSend)) {
        setDraftEditConfirmed(true);
        setInput('');
        setMessages((prev) => [
          ...prev,
          {
            sender: 'assistant',
            text: 'Confirmed. Send the session details when you are ready, and I will edit the existing draft.',
          },
        ]);
      } else {
        setMessages((prev) => [
          ...prev,
          {
            sender: 'assistant',
            text: 'There is already a draft for this note. Reply “yes” if you want me to edit it with AI, or review and sign the draft without changing it.',
          },
        ]);
      }
      return null;
    }

    logger.log('[sendMessage] session=%s input_len=%d', currentSessionId, textToSend.length);
    
    setError(null);
    setCurrentPage(1);
    setHasMoreMessages(false);

    if (textToSend === "__INIT__") {
      const welcome = WELCOME_MESSAGES[Math.floor(Math.random() * WELCOME_MESSAGES.length)];
      const assistantMessage = { sender: 'assistant', text: welcome, options: WELCOME_OPTIONS };
      setMessages([assistantMessage]);
      return null;
    }

    logger.log("🧹 [successMessages] Clearing success messages");
    setSuccessMessages([]);
    setCurrentRequestId(null);

    const messageTimestamp = Date.now();
    const userMessage = { 
      sender: 'user', 
      text: textToSend,
      timestamp: messageTimestamp,
      status: 'sending' as const,
      _notePrefsOverride: notePrefsOverride,
      _overrideContext: overrideContext,
    };
    
    setMessages((prev) => [...prev, userMessage]);
    if (messageText === input) {
      setInput('');
    }

    try {
      const { notePrefs, extra } = buildSendPayload(noteMode, selectedNoteTypes, null);
      
      const finalNotePrefs = notePrefsOverride !== null ? notePrefsOverride : notePrefs;
      
      const baseContext = {
        ...extra,
        patient_id: currentPatient?.id || null,
        patient_name: currentPatient?.name || null,
        encounter_id: currentEncounter?.id || null,
      };
      
      const contextData = overrideContext ? { ...baseContext, ...overrideContext } : baseContext;
      
      const data = await contextSendMessage(textToSend, finalNotePrefs, contextData);

      if (data?.request_id && data?.suggested_action === 'generating_note') {
        setCurrentRequestId(data.request_id as string);
      }

      if (!data) {
        logger.warn("🔄 Message processing skipped or failed");
        // Remove the optimistic user message when processing is intentionally
        // skipped (e.g. session rehydrating, already processed). Only actual
        // errors (caught below) mark messages as 'failed' with a retry button.
        setMessages((prev) => prev.filter(m => 
          !(m.timestamp === messageTimestamp && m.sender === 'user')
        ));
        return null;
      }
      
      logger.log("🔍 [successMessages] Checking for notes in API response:",
        data.note_content ? "Found note_content" : "No note_content",
        data.document_type ? `(type: ${getNoteTypeLabel(data.document_type, undefined, data.template_name)})` : "");

      // Mark user message as sent successfully
      setMessages((prev) => prev.map(m => 
        m.timestamp === messageTimestamp && m.sender === 'user'
          ? { ...m, status: 'sent' as const, request_id: data.request_id }
          : m
      ));

      const assistantMessage = {
        sender: 'assistant',
        text: data.response,
        suggested_action: data.suggested_action,
        ai_mode: data.ai_mode,
        options: data.options,
        encounter_options: data.encounter_options,
        patient_options: data.patient_options,
        note_type_options: data.note_type_options,
        template_options: data.template_options,
        default_template_id: data.default_template_id,
        note_data: data.note_data,
      };
      setMessages((prev) => [...prev, assistantMessage]);

      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

      if (data.note_content && data.document_type) {
        if (data.note_id) {
          const savedNoteId = data.note_id;
          logger.log("🆔 Note ID saved:", savedNoteId);

          setViewingNoteInline(false);
          setInlineNoteData(null);

          addNoteCreatedNotification(data.patient_display || data.client_name || "Unknown", data.document_type, savedNoteId);

          const newSuccessMessage = {
            clientName: data.patient_display || data.client_name || "Unknown",
            noteType: data.document_type,
            noteId: savedNoteId,
            date: nowUtc().toDate().toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
          };

          setSuccessMessages(prevMessages => {
            const isDuplicate = prevMessages.some(msg => msg.noteId === savedNoteId);
            if (isDuplicate) {
              logger.log("🔄 [successMessages] Skipping duplicate note:", savedNoteId);
              return prevMessages;
            }
            logger.log('[successMessages] Adding new success message: noteId=%s noteType=%s', newSuccessMessage.noteId, newSuccessMessage.noteType);
            return [...prevMessages, newSuccessMessage];
          });

          toast.success(`✅ ${getNoteTypeLabel(data.document_type, undefined, data.template_name) || 'Note'} saved for ${data.patient_display || data.client_name || "Unknown"}`);

          logger.log("🧹 Note saved successfully");
        } else {
          logger.log("⚠️ Note data received but no note_id");
        }
      }

      return data;
    } catch (err) {
      logger.error('Error sending message:', err);
      // Mark the user message as failed so the retry button appears
      setMessages((prev) => prev.map(m => 
        m.timestamp === messageTimestamp && m.sender === 'user'
          ? { ...m, status: 'failed' as const }
          : m
      ));
      return null;
    }
  }, [
    input,
    setInput,
    setMessages,
    noteMode,
    selectedNoteTypes,
    contextSendMessage,
    currentSessionId,
    currentPatient,
    currentEncounter,
    setError,
    setCurrentPage,
    setHasMoreMessages,
    setSuccessMessages,
    addNoteCreatedNotification,
    setViewingNoteInline,
    setInlineNoteData,
    messagesEndRef,
    setCurrentRequestId,
    draftEditConfirmed,
  ]);

  const fetchNoteData = useCallback(async (noteId) => {
    try {
      setNoteError(null);
      const res = await getWithAuth(`/api/notes/${noteId}`);

      if (!res.ok) {
        if (res.status === 401) {
          throw new Error("Your session has expired. Please log in again.");
        } else if (res.status === 403) {
          throw new Error("You don't have permission to access this note.");
        } else {
          throw new Error(`Failed to load note: ${res.statusText}`);
        }
      }

      const data = await res.json();
      setInlineNoteData(data);
      setViewingNoteInline(true);
      return data;
    } catch (err) {
      logger.error("Failed to load note:", err);
      setNoteError({
        noteId,
        message: err.message || "The note could not be found or has been deleted."
      });

      if (err.message.includes("session has expired")) {
        toast.error(err.message);
      }
      return null;
    }
  }, [setInlineNoteData, setViewingNoteInline]);

  const renderMessageWithViewNoteLinks = useCallback((text) => {
    if (!text) return '';

    const viewNoteRegex = /<view_note id="([^"]+)">([^<]+)<\/view_note>/g;

    const processedText = text.replace(viewNoteRegex, (match, noteId) => {
      return `<span class="view-note-text">${match.substring(0, match.indexOf('<view_note'))}<button class="view-note-link" data-note-id="${noteId}">View Note</button></span>`;
    });

    const parsedContent = parse(processedText, {
      replace: (domNode) => {
        const node = domNode as unknown as Record<string, unknown>;
        if (node.name === 'button' && (node.attribs as Record<string, string>)?.class === 'view-note-link') {
          const noteId = (node.attribs as Record<string, string>)['data-note-id'];
          return (
            <button
              className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
              onClick={() => fetchNoteData(noteId)}
            >
              View Note
            </button>
          );
        }
        return domNode;
      }
    });

    return parsedContent;
  }, [fetchNoteData]);

  const retryMessage = useCallback(async (messageText: string, notePrefsOverride = null, overrideContext = null, originalTimestamp?: number) => {
    // Remove the failed message before resending
    if (originalTimestamp) {
      setMessages((prev) => prev.filter(m => !(m.timestamp === originalTimestamp && m.sender === 'user')));
    }
    // Resend the message using the same sendMessage flow
    return sendMessage(messageText, notePrefsOverride, overrideContext);
  }, [sendMessage, setMessages]);

  return {
    sendMessage,
    retryMessage,
    fetchNoteData,
    renderMessageWithViewNoteLinks,
    successMessages,
    setSuccessMessages,
    noteError,
    setNoteError,
    currentRequestId,
    clearCurrentRequestId,
    setCurrentRequestId
  };
}
